import { useQuery } from "@tanstack/react-query";
import { Link, useParams } from "react-router-dom";
import { Check, FileText, ChevronRight, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { Alert, Button, Card, DataField, PageContainer } from "@/components/ui";

interface SolicitudResumen {
  id: string;
  folio: string;
  titularRazonSocial: string;
  tramitadorRazonSocial: string | null;
  servicioCodigo: string | null;
  productoNombre: string;
  productoModelo: string;
  marca: string;
  normaCodigo: string;
  estatus: string;
  fechaCreacion: string;
}

/**
 * Pantalla de confirmación post-creación de una Solicitud Telecom. Recupera
 * los datos por GET al detalle (que ya devuelve folio asignado y resumen) y
 * presenta al usuario el folio + un resumen de lo que capturó, con botón
 * "Aceptar" que regresa a Mis Empresas. Equivalente a la pantalla Success
 * del portlet legacy SolCertConfUnica.
 */
export function SolicitudTelecomSuccessPage() {
  const { id } = useParams<{ id: string }>();

  const q = useQuery<SolicitudResumen>({
    queryKey: ["solicitud-telecom-resumen", id],
    queryFn: async () =>
      (await api.get<SolicitudResumen>(`/api/sector/telecom/solicitudes/${id}`)).data,
    enabled: !!id,
  });

  if (q.isLoading) {
    return (
      <div className="flex items-center justify-center py-20 text-fg-subtle">
        <Loader2 className="mr-2 h-5 w-5 animate-spin" /> Cargando solicitud…
      </div>
    );
  }
  if (q.error || !q.data) {
    return <Alert variant="warning">No se pudo cargar el resumen de la solicitud.</Alert>;
  }

  const s = q.data;

  return (
    <PageContainer width="form">
      <section className="rounded-xl border border-estado-ok/25 bg-estado-ok-bg p-6 shadow-card">
        <div className="flex items-start gap-3">
          <div className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-estado-ok text-white">
            <Check className="h-5 w-5" />
          </div>
          <div className="flex-1">
            <h1 className="text-xl font-bold text-estado-ok">
              Solicitud registrada correctamente
            </h1>
            <p className="mt-1 text-sm text-estado-ok">
              Tu solicitud quedó en revisión por el equipo de Analistas. Recibirás un correo
              en cada cambio de estado del trámite (revisión, facturación, evaluación y
              toma de decisión).
            </p>
          </div>
        </div>

        <dl className="mt-5 grid gap-4 sm:grid-cols-2">
          <DataField label="Folio" value={<span className="font-mono text-base font-semibold text-fg">{s.folio}</span>} />
          <DataField label="Estatus" value={s.estatus} />
          <DataField label="Fecha de solicitud" value={formatFecha(s.fechaCreacion)} />
          <DataField label="Tipo de trámite" value={s.servicioCodigo ?? "—"} />
        </dl>
      </section>

      <Card title="Resumen">
        <dl className="grid gap-4 sm:grid-cols-2">
          <DataField label="Titular" value={s.titularRazonSocial} />
          {s.tramitadorRazonSocial && (
            <DataField label="Tramitador externo" value={s.tramitadorRazonSocial} />
          )}
          <DataField label="Producto" value={s.productoNombre} />
          <DataField label="Modelo" value={s.productoModelo} />
          <DataField label="Marca" value={s.marca} />
          <DataField label="NOM/DT principal" value={<span className="font-mono">{s.normaCodigo}</span>} />
        </dl>
      </Card>

      <div className="flex flex-wrap justify-between gap-2">
        <Link to="/telecom">
          <Button variant="secondary" icon={<Check className="h-4 w-4" />}>
            Aceptar (volver a Telecomunicaciones)
          </Button>
        </Link>
        <Link to={`/sector/telecom/solicitudes/${s.id}`}>
          <Button icon={<FileText className="h-4 w-4" />}>
            Ver detalle
            <ChevronRight className="h-4 w-4" />
          </Button>
        </Link>
      </div>
    </PageContainer>
  );
}

function formatFecha(s: string): string {
  try {
    return new Date(s).toLocaleString("es-MX", { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return s;
  }
}
